import { Toast, ToastTitle } from '@fluentui/react-components';
import { useCallback, useEffect, useState } from 'react';

import { useHttpClient } from '../../../api/HttpClientContext';
import { useGraderActions } from '../../../context/GraderContext';
import { useCanEditResource } from '../../../hooks/useCanEditResource/useCanEditResource.js';
import type { Grader } from '../../../hooks/useGraders/useGraders.interface';
import {
  GRADER_MODEL_VAR_REF,
  type JudgeGraderSpec,
  type UpdateGraderRequest,
} from '../../../services/grader.interface';
import { WorkflowApiService } from '../../../services/workflowApi';
import { useUserBi } from '../../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../../telemetry/userbi.js';
import type { BareTemplateToken } from '../../../utils/templateRefs.js';
import type { VariableRow } from '../../AgentsPage/AgentForm/AgentForm.types.js';
import { variableRowsToMap } from '../../AgentsPage/AgentForm/AgentForm.utils.js';
import { useGraderTemplateGuidance } from '../graderCreate/useGraderTemplateGuidance.js';
import {
  variableMapsEqual,
  variableRowsFromMap,
} from '../graderVariables.utils.js';
import { useVariableRows } from '../useVariableRows.js';

/**
 * Edit state + persistence for a single grader. Mirrors the
 * `useCreateGraderForm` shape: local field state, a save that talks to the
 * workflow API, and toasts on success/failure. Editable fields are the
 * grader's identity, model configuration, and prompts. The immutable resource
 * `name` is never edited here.
 *
 * Editing is gated by the shared `useCanEditResource` rule: `canEdit` is false
 * (and `start` is a no-op) unless the workspace is public, or the signed-in
 * user is the grader's creator or the workspace creator.
 */
export interface UseEditGraderResult {
  /** True when the signed-in user created this grader. */
  canEdit: boolean;
  isEditing: boolean;
  saving: boolean;
  displayName: string;
  setDisplayName: (value: string) => void;
  systemPrompt: string;
  setSystemPrompt: (value: string) => void;
  userTemplate: string;
  setUserTemplate: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  modelName: string;
  setModelName: (value: string) => void;
  temperature: string;
  setTemperature: (value: string) => void;
  topP: string;
  setTopP: (value: string) => void;
  maxTokens: string;
  setMaxTokens: (value: string) => void;
  variables: VariableRow[];
  variableErrors: Record<string, string>;
  updateVariable: (id: string, patch: Partial<VariableRow>) => void;
  addVariable: () => void;
  declareVariable: (name: string) => void;
  removeVariable: (id: string) => void;
  /** `$var` names referenced in the edited prompts but not declared. */
  undeclaredVars: string[];
  /** Bare `{{…}}` tokens found across the edited prompt fields. */
  bareTokens: BareTemplateToken[];
  templateFieldLabels: Record<string, string>;
  convertBareToken: (token: BareTemplateToken) => void;
  /** True when the edited fields differ from the persisted judge. */
  dirty: boolean;
  start: () => void;
  cancel: () => void;
  save: () => void;
}

export function useEditGrader(
  judge: Grader,
  workspaceName: string,
): UseEditGraderResult {
  const client = useHttpClient();
  const { trackOperation } = useUserBi();
  const { patchGrader, dispatchToast } = useGraderActions();

  // Edit/delete gating — see canEditResource.
  const canEditResource = useCanEditResource({
    workspaceName,
    resourceCreatedBy: judge.createdBy,
  });
  const canEdit =
    canEditResource &&
    (judge.graderType === undefined || judge.graderType === 'LlmGraderSpec');

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userTemplate, setUserTemplate] = useState('');
  const [description, setDescription] = useState('');
  const [modelName, setModelName] = useState('');
  const [temperature, setTemperature] = useState('');
  const [topP, setTopP] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const {
    variables,
    setVariables,
    variableErrors,
    updateVariable,
    addVariable,
    declareVariable,
    removeVariable,
  } = useVariableRows();

  // Selecting a different grader (same component instance) must drop any
  // in-flight edit so we never save one grader's edits onto another. Reset on
  // workspace too: a same-id grader in another workspace is a different
  // resource, and save() targets `workspaceName`.
  useEffect(() => {
    setIsEditing(false);
  }, [judge.id, workspaceName]);

  const start = useCallback(() => {
    if (!canEdit) return;
    setDisplayName(judge.displayName ?? '');
    setSystemPrompt(judge.systemPrompt ?? '');
    setUserTemplate(judge.userTemplate ?? '');
    setDescription(judge.description ?? '');
    setModelName(judge.model ?? GRADER_MODEL_VAR_REF);
    setTemperature(judge.temperature ?? '');
    setTopP(judge.topP ?? '');
    setMaxTokens(judge.maxTokens ?? '');
    setVariables(variableRowsFromMap(judge.variables));
    setIsEditing(true);
  }, [
    canEdit,
    judge.displayName,
    judge.systemPrompt,
    judge.userTemplate,
    judge.description,
    judge.model,
    judge.temperature,
    judge.topP,
    judge.maxTokens,
    judge.variables,
    setVariables,
  ]);

  const cancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const promptChanged =
    systemPrompt !== (judge.systemPrompt ?? '') ||
    userTemplate !== (judge.userTemplate ?? '');
  const modelChanged =
    modelName !== (judge.model ?? GRADER_MODEL_VAR_REF) ||
    temperature !== (judge.temperature ?? '') ||
    topP !== (judge.topP ?? '') ||
    maxTokens !== (judge.maxTokens ?? '');
  const displayNameChanged = displayName !== (judge.displayName ?? '');
  const descriptionChanged = description !== (judge.description ?? '');
  const variablesChanged = !variableMapsEqual(
    variableRowsToMap(variables),
    judge.variables,
  );

  // Template-reference awareness over the editable LLM prompt fields (this
  // editor only authors LLM judges). Feeds the Variables-card callouts and
  // gates Save on referenced-but-undeclared $var, mirroring the create flow.
  const guidance = useGraderTemplateGuidance(
    [
      {
        field: 'modelName',
        label: 'Model name',
        text: modelName,
        setText: setModelName,
      },
      {
        field: 'systemPrompt',
        label: 'System prompt',
        text: systemPrompt,
        setText: setSystemPrompt,
      },
      {
        field: 'temperature',
        label: 'Temperature',
        text: temperature,
        setText: setTemperature,
      },
      { field: 'topP', label: 'Top P', text: topP, setText: setTopP },
      {
        field: 'maxTokens',
        label: 'Max tokens',
        text: maxTokens,
        setText: setMaxTokens,
      },
      {
        field: 'userMessage',
        label: 'User message',
        text: userTemplate,
        setText: setUserTemplate,
      },
    ],
    variables,
  );

  const dirty =
    isEditing &&
    (displayNameChanged ||
      descriptionChanged ||
      modelChanged ||
      promptChanged ||
      variablesChanged);

  const save = useCallback(() => {
    if (
      !canEdit ||
      !isEditing ||
      saving ||
      Object.keys(variableErrors).length > 0 ||
      guidance.undeclaredVars.length > 0
    ) {
      return;
    }

    // Determine which fields changed from the untrimmed edit state (same basis
    // as `dirty`). Trimming happens only when persisting a changed field, so an
    // untouched field that already carries surrounding whitespace is never
    // flagged as edited and overwritten with a trimmed value.
    const promptEdited =
      systemPrompt !== (judge.systemPrompt ?? '') ||
      userTemplate !== (judge.userTemplate ?? '');
    const modelEdited =
      modelName !== (judge.model ?? GRADER_MODEL_VAR_REF) ||
      temperature !== (judge.temperature ?? '') ||
      topP !== (judge.topP ?? '') ||
      maxTokens !== (judge.maxTokens ?? '');
    const displayNameEdited = displayName !== (judge.displayName ?? '');
    const descriptionEdited = description !== (judge.description ?? '');
    const variablesEdited = !variableMapsEqual(
      variableRowsToMap(variables),
      judge.variables,
    );

    // Nothing to persist — just leave edit mode (the Save button is disabled
    // in this state, so this only guards programmatic calls).
    if (
      !displayNameEdited &&
      !descriptionEdited &&
      !modelEdited &&
      !promptEdited &&
      !variablesEdited
    ) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    void (async () => {
      const service = new WorkflowApiService(client);
      try {
        const payload: UpdateGraderRequest = {};
        const patch: Partial<Grader> = {};

        if (displayNameEdited) {
          const nextDisplayName = displayName.trim();
          payload.displayName = nextDisplayName;
          patch.displayName = nextDisplayName;
        }
        if (descriptionEdited) {
          const nextDescription = description.trim();
          payload.description = nextDescription;
          patch.description = nextDescription;
        }
        const nextVariables = variablesEdited
          ? variableRowsToMap(variables)
          : undefined;
        if (variablesEdited) {
          payload.variables = nextVariables;
          patch.variables = nextVariables;
        }
        await trackOperation(
          {
            panelType: PanelType.GradersPage,
            entityType: EntityType.Grader,
            operation: OperationType.Update,
          },
          async () => {
            if (promptEdited || modelEdited) {
              const nextModelName = modelName.trim() || GRADER_MODEL_VAR_REF;
              const nextSystemPrompt = systemPrompt.trim();
              const nextUserTemplate = userTemplate.trim();
              const nextTemperature = temperature.trim();
              const nextTopP = topP.trim();
              const nextMaxTokens = maxTokens.trim();
              // PUT replaces the grader, so re-read and preserve fields that
              // this editor does not own.
              const current = await service.getGrader(
                workspaceName,
                judge.name,
              );
              const currentLlmSpec =
                current.spec?.graderType === 'LlmGraderSpec'
                  ? current.spec
                  : undefined;
              if (!currentLlmSpec) {
                throw new Error('Cannot replace a non-LLM grader spec');
              }
              const currentInit = currentLlmSpec?.init ?? {};
              const spec: JudgeGraderSpec = {
                graderType: 'LlmGraderSpec',
                init: {
                  ...currentInit,
                  modelName: modelEdited
                    ? nextModelName
                    : (currentInit.modelName ?? GRADER_MODEL_VAR_REF),
                  systemPrompt: promptEdited
                    ? nextSystemPrompt
                    : currentInit.systemPrompt,
                  ...(modelEdited
                    ? {
                        temperature:
                          nextTemperature.length > 0
                            ? nextTemperature
                            : undefined,
                        topP: nextTopP.length > 0 ? nextTopP : undefined,
                        maxTokens:
                          nextMaxTokens.length > 0 ? nextMaxTokens : undefined,
                      }
                    : {}),
                },
                ...(promptEdited
                  ? nextUserTemplate.length > 0
                    ? { invoke: { userMessage: nextUserTemplate } }
                    : {}
                  : currentLlmSpec?.invoke
                    ? { invoke: currentLlmSpec.invoke }
                    : {}),
              };
              await service.upsertGrader(workspaceName, judge.name, {
                displayName: displayNameEdited
                  ? displayName.trim()
                  : current.displayName,
                description: descriptionEdited
                  ? description.trim()
                  : current.description,
                spec,
                variables: nextVariables ?? current.variables,
                metadata: current.metadata,
              });
              if (promptEdited) {
                patch.systemPrompt = nextSystemPrompt;
                patch.userTemplate =
                  nextUserTemplate.length > 0 ? nextUserTemplate : undefined;
              }
              if (modelEdited) {
                patch.model = nextModelName;
                patch.temperature =
                  nextTemperature.length > 0 ? nextTemperature : undefined;
                patch.topP = nextTopP.length > 0 ? nextTopP : undefined;
                patch.maxTokens =
                  nextMaxTokens.length > 0 ? nextMaxTokens : undefined;
              }
              return;
            }

            await service.updateGrader(workspaceName, judge.name, payload);
          },
        );

        patch.isDraft = true;
        patchGrader(judge.id, patch);
        setIsEditing(false);
        dispatchToast(
          <Toast>
            <ToastTitle>Judge updated</ToastTitle>
          </Toast>,
          { intent: 'success', timeout: 5000 },
        );
      } catch {
        dispatchToast(
          <Toast>
            <ToastTitle>Failed to update judge</ToastTitle>
          </Toast>,
          { intent: 'error', timeout: 5000 },
        );
      } finally {
        setSaving(false);
      }
    })();
  }, [
    saving,
    canEdit,
    isEditing,
    displayName,
    systemPrompt,
    userTemplate,
    description,
    modelName,
    temperature,
    topP,
    maxTokens,
    variables,
    variableErrors,
    guidance.undeclaredVars.length,
    client,
    workspaceName,
    judge.id,
    judge.name,
    judge.systemPrompt,
    judge.userTemplate,
    judge.description,
    judge.displayName,
    judge.model,
    judge.temperature,
    judge.topP,
    judge.maxTokens,
    judge.variables,
    patchGrader,
    dispatchToast,
    trackOperation,
  ]);

  return {
    canEdit,
    isEditing,
    saving,
    displayName,
    setDisplayName,
    systemPrompt,
    setSystemPrompt,
    userTemplate,
    setUserTemplate,
    description,
    setDescription,
    modelName,
    setModelName,
    temperature,
    setTemperature,
    topP,
    setTopP,
    maxTokens,
    setMaxTokens,
    variables,
    variableErrors,
    updateVariable,
    addVariable,
    declareVariable,
    removeVariable,
    undeclaredVars: guidance.undeclaredVars,
    bareTokens: guidance.bareTokens,
    templateFieldLabels: guidance.templateFieldLabels,
    convertBareToken: guidance.convertBareToken,
    dirty,
    start,
    cancel,
    save,
  };
}
